'use client';

import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  BreadcrumbsPadding,
  Container,
  SearchBodyContainer
} from 'components/Container/Container.css';
import { TabsNavigationSearch } from 'components/TabsNavigation';
import SearchEvent from 'templates/Search/SearchEvent';
import SearchPlaces from 'templates/Search/SearchPlaces';
import SearchProducers from 'templates/Search/SearchProducers';

import { SearchEventResult } from 'searchEvent.d';
import { ParamsResult } from 'ParamsResult.d';
import { SearchProducersResult } from 'searchProducers.d';
import { SearchPlacesResult } from 'searchPlaces.d';
import { tabsSearch } from 'constants/search';
import { TabItem } from 'components/TabsNavigation/TabsNavigation';
import { buildBreadcrumbList } from 'components/ClosedSearchSubHeader/ClosedSearchSubHeader';
import searchClient from 'utils/algolia';
import { InstantSearch } from 'react-instantsearch';
import { ALGOLIA_INDEX_NAME } from 'constants/algolia';
import { Location } from 'location';
import { Breadcrumbs } from '@sympla/sympla-components';
import { AlgoliaSearchEvents } from './AlgoliaSearchEvents/AlgoliaSearchEvents';

import { useLastSearches } from 'hooks/useLastSearches';

export type SearchEventTemplateProps = {
  params: ParamsResult;
  searchEventsResult: SearchEventResult;
  otherSearchDataResult?: SearchEventResult;
  searchPlaceResult: SearchPlacesResult;
  searchOrganizerResult: SearchProducersResult;
  termURL: string;
  isMobile: boolean;
  dateURL?: string | string[];
  tabURL?: string | string[];
  location: Location;
};

function SearchWithTermTemplate({
  params,
  searchEventsResult,
  otherSearchDataResult,
  searchPlaceResult,
  searchOrganizerResult,
  dateURL,
  tabURL,
  termURL,
  isMobile,
  location
}: SearchEventTemplateProps) {
  const [selectedTab, setSelectedTab] = useState(
    tabURL || tabsSearch.event.tabName
  );

  const { updateLastSearches } = useLastSearches();

  useEffect(() => {
    if (termURL && termURL.trim().length > 0) {
      updateLastSearches(termURL);
    }
  }, []);

  const buildTemplate = () => {
    switch (selectedTab) {
      case tabsSearch.place.tabName:
        return (
          <SearchPlaces
            searchDataResult={searchPlaceResult}
            params={params}
            isMobile={isMobile}
          />
        );
      case tabsSearch.organizer.tabName:
        return (
          <SearchProducers
            searchDataResult={searchOrganizerResult}
            params={params}
            isMobile={isMobile}
          />
        );
      default:
        return (
          <SearchEvent
            searchDataResult={searchEventsResult}
            otherSearchDataResult={otherSearchDataResult}
            params={params}
            dateURL={dateURL}
            termURL={termURL}
            isMobile={isMobile}
          />
        );
    }
  };

  const handleSelectTab = (item: TabItem) => {
    setSelectedTab(item?.tabName);
  };

  // TODO: REFACTOR AFTER CHANGE 100% ALGOLIA SEARCH TO ADD IT IN SWITCH CASE ABOVE AND NOT REPEAT TABS NAVIGATION CODE.
  // CURRENTLY, WE HAVE TO DUPLICATE THE TABS NAVIGATION FOR THE ALGOLIA SEARCH BAR.
  if (selectedTab === tabsSearch.event.tabName) {
    return (
      <div>
        <InstantSearch
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX_NAME}
          insights
        >
          <AlgoliaSearchEvents
            params={params}
            handleChangeTabs={handleSelectTab}
            location={location}
            termURL={termURL}
          />
        </InstantSearch>
      </div>
    );
  }

  return (
    <div>
      <div className={BreadcrumbsPadding}>
        <Breadcrumbs list={buildBreadcrumbList(params)} />
      </div>
      <TabsNavigationSearch
        hasFilter={selectedTab === tabsSearch.event.tabName}
        handleChange={handleSelectTab}
      />

      <div className={SearchBodyContainer}>
        <div className={Container}>{buildTemplate()}</div>
      </div>
    </div>
  );
}

export default observer(SearchWithTermTemplate);
